import { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../@/components/ui/select";
import { FileIcon, Trash2 } from "lucide-react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { toast } from "sonner";
import { ClipLoader } from "react-spinners";

const SUPPORTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const parseCsvFile = (file) =>
    new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim().toLowerCase(),
            complete: ({ data }) => resolve(data),
            error: reject,
        });
    });

const parseSpreadsheet = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const workbook = XLSX.read(new Uint8Array(reader.result), { type: "array" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            resolve(XLSX.utils.sheet_to_json(worksheet));
        };
        reader.readAsArrayBuffer(file);
    });

const parseFile = (file) =>
    file.name.toLowerCase().endsWith(".csv") ? parseCsvFile(file) : parseSpreadsheet(file);

const normaliseEmail = (row = {}) =>
    (row.email ?? row.Email ?? row.emails ?? row.Emails ?? row["e-mail"] ?? "")
        .toString()
        .trim()
        .toLowerCase();

export default function PathwayBulkEnrollDialog({
    open,
    onOpenChange,
    pathwayOptions = [],
    onUploaded,
    templateUrl = `https://lms-internal.zinduaschool.com/templates/bulk-enroll-emails.csv`,
}) {
    const [selectedPathway, setSelectedPathway] = useState("");
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [allStudents, setAllStudents] = useState([]);
    const [pathwayEnrolledIds, setPathwayEnrolledIds] = useState([]);
    const fileInputRef = useRef(null);
    const { getToken } = useAuth();

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const token = await getToken();
                const users = await fetchAllPaginatedItems({
                    url: `${api_endpoint}/user/users`,
                    token,
                    selectItems: (data) => data?.users ?? [],
                    mode: "skip",
                    pageSize: 200,
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAllStudents(users ?? []);
            } catch (error) {
                console.error("Failed to fetch users", error);
                setAllStudents([]);
            }
        })();
    }, [open, getToken]);

    useEffect(() => {
        if (!open || !selectedPathway) {
            setPathwayEnrolledIds([]);
            return;
        }

        (async () => {
            try {
                const token = await getToken();
                if (!token) throw new Error("No token");
                const reqConfig = {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                };
                const response = await axios.get(`${api_endpoint}/pathway/enrollments/${selectedPathway}`, reqConfig);
                const enrolledIds = (response.data?.enrolments || []).map((item) => item.user_id).filter(Boolean);
                setPathwayEnrolledIds(enrolledIds);
            } catch (error) {
                console.error("Failed to fetch pathway enrolments", error);
                setPathwayEnrolledIds([]);
            }
        })();
    }, [open, selectedPathway, getToken]);

    const addFiles = (incomingList) => {
        if (!incomingList?.length) return;
        setFiles((prev) => {
            const seen = new Set(prev.map((file) => `${file.name}-${file.size}`));
            const next = Array.from(incomingList).filter((file) => {
                const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
                if (!SUPPORTED_EXTENSIONS.includes(extension)) {
                    toast.error(`Unsupported file type: ${extension}. Please upload CSV or Excel files.`);
                    return false;
                }
                const key = `${file.name}-${file.size}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            return [...prev, ...next];
        });
    };

    const onFileChange = (event) => addFiles(event.target.files);

    const onDrop = (event) => {
        event.preventDefault();
        addFiles(event.dataTransfer.files);
    };

    const removeFile = (name, size) =>
        setFiles((prev) => prev.filter((file) => file.name !== name || file.size !== size));

    const handleUpload = async () => {
        if (!files.length || !selectedPathway) {
            toast.error("Select a pathway and at least one file before uploading.");
            return;
        }
        setUploading(true);
        try {
            const token = await getToken();
            const parsedRows = (await Promise.all(files.map((file) => parseFile(file)))).flat();
            const emails = parsedRows.map(normaliseEmail).filter(Boolean);

            if (!emails.length) {
                toast.error("No valid emails detected in the uploaded files.");
                setUploading(false);
                return;
            }

            const emailToId = new Map(
                allStudents.map((student) => [student.email?.trim().toLowerCase(), student.id])
            );

            const matchedIds = [];
            const missingEmails = [];
            emails.forEach((email) => {
                const id = emailToId.get(email);
                if (id) {
                    matchedIds.push(id);
                } else {
                    missingEmails.push(email);
                }
            });

            if (!matchedIds.length) {
                toast.error("No matching users found for the uploaded emails.");
                setUploading(false);
                return;
            }

            const uniqueMatchedIds = Array.from(new Set(matchedIds));
            const enrolledSet = new Set((pathwayEnrolledIds || []).map((id) => Number(id)));
            const alreadyEnrolledIds = uniqueMatchedIds.filter((id) => enrolledSet.has(Number(id)));
            const newEnrollmentIds = uniqueMatchedIds.filter((id) => !enrolledSet.has(Number(id)));
            const missingCount = new Set(missingEmails).size;

            if (!newEnrollmentIds.length) {
                toast.info(
                    `${alreadyEnrolledIds.length} already enrolled. ${missingCount} emails not found. No new students added.`
                );
                setUploading(false);
                return;
            }

            await axios.post(
                `${api_endpoint}/pathway/batch-enroll`,
                {
                    pathway_id: Number(selectedPathway),
                    user_ids: newEnrollmentIds,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                `${newEnrollmentIds.length} students enrolled. ${alreadyEnrolledIds.length} already enrolled. ${missingCount} emails not found.`
            );
            setFiles([]);
            setSelectedPathway("");
            setPathwayEnrolledIds([]);
            onOpenChange?.(false);
            onUploaded?.();
        } catch (error) {
            console.error("Bulk pathway enrolment failed", error);
            toast.error(error?.response?.data?.message ?? "Failed to enrol students. Try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(value) => {
                onOpenChange(value);
                if (!value) {
                    setFiles([]);
                    setSelectedPathway("");
                    setUploading(false);
                    setPathwayEnrolledIds([]);
                }
            }}
        >
            <DialogContent className="max-w-lg w-[95vw] bg-(--surface-base) text-(--neutral-900)">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-heading">Bulk Enrol Students to Pathway</DialogTitle>
                    <DialogDescription className="text-sm text-(--neutral-600)">
                        Upload CSV file of students to bulk enrol.{' '}
                        <a
                            href={templateUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-(--color-primary) underline"
                        >
                            Click to download template
                        </a>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <p className="text-sm text-(--neutral-500)">Select the pathway before confirming.</p>
                        <label className="text-sm font-medium text-(--neutral-800)">Select Pathway</label>
                        <Select value={selectedPathway} onValueChange={setSelectedPathway}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Choose pathway" />
                            </SelectTrigger>
                            <SelectContent>
                                {pathwayOptions.length === 0 ? (
                                    <SelectItem value="" disabled>
                                        No pathways available
                                    </SelectItem>
                                ) : (
                                    pathwayOptions.map((pathway) => (
                                        <SelectItem key={pathway.id} value={String(pathway.id)}>
                                            {pathway.name}
                                        </SelectItem>
                                    ))
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    <div
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={onDrop}
                        className="flex flex-col items-center justify-center gap-3 border border-dashed border-(--neutral-300) rounded-lg py-10 px-4 text-center bg-(--surface-base) text-(--neutral-600)"
                    >
                        <FileIcon className="w-10 h-10 text-(--neutral-400)" />
                        <p className="font-heading text-(--neutral-800)">
                            Drag and drop or{' '}
                            <button
                                type="button"
                                className="text-(--color-primary) underline"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Choose file
                            </button>{' '}
                            to upload
                        </p>
                        <Input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xls,.xlsx"
                            multiple
                            className="hidden"
                            onChange={onFileChange}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-(--neutral-500)">
                        <p className="text-sm">Accepted file types: CSV, XLS, XLSX</p>
                        <p className="text-sm">Max file size: 10MB</p>
                    </div>

                    {files.length > 0 && (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {files.map((file) => (
                                <div
                                    key={`${file.name}-${file.size}`}
                                    className="flex items-center justify-between border border-(--neutral-200) rounded-md px-3 py-2 bg-(--neutral-100)"
                                >
                                    <div className="flex items-center gap-3">
                                        <FileIcon className="w-5 h-5 text-(--color-primary)" />
                                        <div>
                                            <p className="text-sm font-medium">{file.name}</p>
                                            <p className="text-xs text-(--neutral-500)">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="text-(--neutral-500) hover:text-(--color-destructive)"
                                        onClick={() => removeFile(file.name, file.size)}
                                        aria-label="Remove file"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                            setFiles([]);
                            setSelectedPathway("");
                            setPathwayEnrolledIds([]);
                        }}
                        disabled={uploading}
                    >
                        Cancel
                    </Button>
                    <Button type="button" className="" onClick={handleUpload} disabled={!files.length || uploading}>
                        {uploading ? (
                            <span className="flex items-center gap-2">
                                <ClipLoader size={18} color="#fff" />
                            </span>
                        ) : (
                            "Confirm Upload"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
